import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  Stack,
  Button,
  TextField,
  Switch,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Divider,
  Alert,
  CircularProgress,
  Tooltip,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  LinearProgress,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import UploadIcon from "@mui/icons-material/Upload";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import dayjs from "dayjs";
import useSimApi from "src/hooks/useSimApi";
import SaveAsDialog from "./SaveAsDialog";

const SIMULATOR_TYPE = process.env.REACT_APP_SIMULATOR_TYPE || "target";

const OAUTH_MODELS = ["OAUTH2_CLIENT_CREDENTIALS", "OAUTH2_AUTHORIZATION_CODE", "BEARER_TOKEN"];

// Maps authorization_type values from suggest-info to simulator auth_model values
const AUTH_TYPE_MAP: Record<string, string> = {
  BEARER_TOKEN: "BEARER_TOKEN",
  BASIC: "BASIC",
  BASIC_WITH_SESSION_TOKEN: "BASIC_WITH_SESSION_TOKEN",
  QUERY_STRING: "QUERY_STRING",
};

const AUTH_MODEL_LABELS: Record<string, string> = {
  OAUTH2_CLIENT_CREDENTIALS: "OAuth 2.0 — Client Credentials",
  OAUTH2_AUTHORIZATION_CODE: "OAuth 2.0 — Authorization Code",
  BEARER_TOKEN: "Bearer Token (static)",
  BASIC: "Basic Auth",
  BASIC_WITH_SESSION_TOKEN: "Basic Auth with Session Token",
  QUERY_STRING: "Query String (API key)",
};

// ── Suggest dialog ────────────────────────────────────────────────────────────

interface SuggestedInfo {
  name?: string | null;
  description?: string | null;
  authorization_type?: string | null;
}

interface SuggestField {
  key: keyof SuggestedInfo;
  label: string;
  multiline?: boolean;
}

const SUGGEST_FIELDS: SuggestField[] = [
  { key: "name", label: "Simulator name" },
  { key: "authorization_type", label: "Auth model" },
  { key: "description", label: "Description", multiline: true },
];

type SuggestStep = "idle" | "scanning" | "result" | "error";

interface SuggestDialogProps {
  open: boolean;
  onClose: () => void;
  onApply: (fields: SuggestedInfo) => void;
  request: (path: string, method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE", body?: unknown) => Promise<Response>;
}

const SuggestDialog: React.FC<SuggestDialogProps> = ({ open, onClose, onApply, request }) => {
  const [url, setUrl] = useState("");
  const [step, setStep] = useState<SuggestStep>("idle");
  const [statusMessage, setStatusMessage] = useState("");
  const [progress, setProgress] = useState(0);
  const [streamedText, setStreamedText] = useState("");
  const [suggestions, setSuggestions] = useState<SuggestedInfo>({});
  const [selected, setSelected] = useState<Set<keyof SuggestedInfo>>(new Set());
  const [error, setError] = useState("");

  const reset = () => {
    setStep("idle");
    setStatusMessage("");
    setProgress(0);
    setStreamedText("");
    setSuggestions({});
    setSelected(new Set());
    setError("");
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const toggleField = (key: keyof SuggestedInfo) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  const handleApply = () => {
    const toApply: SuggestedInfo = {};
    for (const key of selected) {
      if (suggestions[key] != null) (toApply as any)[key] = suggestions[key];
    }
    onApply(toApply);
    handleClose();
  };

  const runSuggest = async () => {
    if (!url.trim()) return;
    setStep("scanning");
    setStreamedText("");
    setProgress(0);
    setError("");
    setStatusMessage("Starting…");

    try {
      const resp = await request("/api/v1/suggest", "POST", { url: url.trim() });

      if (!resp.ok || !resp.body) {
        throw new Error(`HTTP ${resp.status}`);
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let accumulated = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() ?? "";

        for (const chunk of parts) {
          const dataLine = chunk.split("\n").find((l) => l.startsWith("data: "));
          if (!dataLine) continue;
          try {
            const event = JSON.parse(dataLine.slice(6));
            if (event.type === "status") {
              setStatusMessage(event.message);
              setProgress((p) => Math.min(p + 15, 40));
            } else if (event.type === "text") {
              accumulated += event.text;
              setStreamedText(accumulated);
              setProgress(45 + Math.min(50, Math.round((accumulated.length / 1500) * 50)));
            } else if (event.type === "done") {
              const result: SuggestedInfo = event.result ?? {};
              if (result.name && !result.name.endsWith("Simulation")) {
                result.name = `${result.name} Simulation`;
              }
              const allKeys = new Set<keyof SuggestedInfo>(
                SUGGEST_FIELDS.map((f) => f.key).filter((k) => result[k] != null) as (keyof SuggestedInfo)[]
              );
              setSuggestions(result);
              setSelected(allKeys);
              setProgress(100);
              setStep("result");
              return;
            } else if (event.type === "error") {
              throw new Error(event.message);
            }
          } catch {
            // ignore malformed SSE lines
          }
        }
      }
    } catch (e: any) {
      setError(e?.message ?? "Unknown error");
      setStep("error");
    }
  };

  const displayValue = (key: keyof SuggestedInfo, val: string) => {
    if (key === "authorization_type") {
      const mapped = AUTH_TYPE_MAP[val];
      return mapped ? (AUTH_MODEL_LABELS[mapped] ?? mapped) : `${val} (not supported in simulator)`;
    }
    return val;
  };

  const selectedCount = selected.size;
  const isScanning = step === "scanning";

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, pb: 1 }}>
        <AutoFixHighIcon sx={{ color: "#7166cf", fontSize: 20 }} />
        <span style={{ fontWeight: 600 }}>Suggest from documentation</span>
      </DialogTitle>
      <Divider />

      <DialogContent sx={{ pt: 2 }}>
        {(step === "idle" || step === "error") && (
          <>
            <Typography variant="body2" sx={{ color: "#6b7280", mb: 2 }}>
              Enter a link to the API documentation. Strawbay will scan and suggest values for the simulator name, description and auth model.
            </Typography>
            <TextField
              label="Documentation URL"
              size="small"
              fullWidth
              placeholder="https://developers.example.com"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && url.trim()) runSuggest(); }}
              sx={{ "& .MuiOutlinedInput-root": { fontFamily: "monospace", fontSize: 13 } }}
            />
          </>
        )}

        {isScanning && (
          <>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <CircularProgress size={18} sx={{ color: "#7166cf", flexShrink: 0 }} />
              <Typography variant="body2" sx={{ color: "#6b7280" }}>
                {statusMessage || "Starting…"}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{ borderRadius: 1, height: 6, backgroundColor: "#f0f0f0",
                "& .MuiLinearProgress-bar": { backgroundColor: "#7166cf" } }}
            />
            {streamedText && (
              <Box sx={{ mt: 2, p: 1.5, backgroundColor: "#f8f8ff", borderRadius: 1,
                border: "1px solid #e0e0e0", maxHeight: 160, overflow: "auto",
                fontFamily: "monospace", fontSize: 11, color: "#464646",
                whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
                {streamedText}
              </Box>
            )}
          </>
        )}

        {step === "error" && (
          <Box sx={{ display: "flex", gap: 1.5, p: 2, mt: 2, backgroundColor: "#fff5f5",
            borderRadius: 1, border: "1px solid #fecaca" }}>
            <ErrorOutlineIcon sx={{ color: "#c62828", flexShrink: 0, mt: 0.2 }} />
            <Typography variant="body2" sx={{ color: "#c62828" }}>{error}</Typography>
          </Box>
        )}

        {step === "result" && (
          <>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
              <CheckCircleOutlineIcon sx={{ color: "#2e7d32", fontSize: 18 }} />
              <Typography variant="body2" sx={{ fontWeight: 600, color: "#374151" }}>
                Suggestions ready — select which fields to apply
              </Typography>
            </Box>
            <Divider sx={{ mb: 1.5 }} />
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              {SUGGEST_FIELDS.map((f) => {
                const val = suggestions[f.key];
                if (val == null) return null;
                return (
                  <Box key={f.key} sx={{ py: 1, borderBottom: "1px solid #f3f4f6" }}>
                    <FormControlLabel
                      sx={{ alignItems: "flex-start", m: 0, width: "100%" }}
                      control={
                        <Checkbox
                          checked={selected.has(f.key)}
                          onChange={() => toggleField(f.key)}
                          size="small"
                          sx={{ pt: 0, color: "#7166cf", "&.Mui-checked": { color: "#7166cf" } }}
                        />
                      }
                      label={
                        <Box sx={{ ml: 0.5 }}>
                          <Typography variant="caption" sx={{ color: "#6b7280", display: "block", mb: 0.25 }}>
                            {f.label}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "#111827",
                              fontFamily: f.multiline ? "monospace" : undefined,
                              fontSize: f.multiline ? 12 : undefined,
                              whiteSpace: f.multiline ? "pre-wrap" : undefined,
                              maxHeight: f.multiline ? 100 : undefined,
                              overflowY: f.multiline ? "auto" : undefined,
                              backgroundColor: f.multiline ? "#f9fafb" : undefined,
                              p: f.multiline ? 1 : undefined,
                              borderRadius: f.multiline ? 0.5 : undefined,
                              border: f.multiline ? "1px solid #e5e7eb" : undefined,
                            }}
                          >
                            {displayValue(f.key, String(val))}
                          </Typography>
                        </Box>
                      }
                    />
                  </Box>
                );
              })}
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={handleClose} variant="text" sx={{ color: "#6b7280", textTransform: "none" }}>
          Cancel
        </Button>
        {(step === "idle" || step === "error") && (
          <Button
            onClick={runSuggest}
            variant="contained"
            startIcon={<AutoFixHighIcon />}
            disabled={!url.trim()}
            sx={{ backgroundColor: "#7166cf", "&:hover": { backgroundColor: "#5a4dbd" }, textTransform: "none" }}
          >
            Scan
          </Button>
        )}
        {step === "result" && (
          <Button
            onClick={handleApply}
            variant="contained"
            disabled={selectedCount === 0}
            startIcon={<CheckCircleOutlineIcon />}
            sx={{ backgroundColor: "#2e7d32", "&:hover": { backgroundColor: "#1b5e20" }, textTransform: "none" }}
          >
            Apply {selectedCount} field{selectedCount !== 1 ? "s" : ""}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

// ── Settings page ─────────────────────────────────────────────────────────────

const Settings: React.FC = () => {
  const { request } = useSimApi();
  const [config, setConfig] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [saveAsOpen, setSaveAsOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Simulator inventory (for the top dropdown + Simulator Admin list)
  interface SimEntry {
    id: number;
    name: string;
    description: string;
    client_id: string;
    is_active: boolean;
    updated_at: string;
  }
  const [simulators, setSimulators] = useState<SimEntry[]>([]);
  const [switching, setSwitching] = useState(false);
  const [decommissioning, setDecommissioning] = useState<number | null>(null);
  const [adminSelectedId, setAdminSelectedId] = useState<number | "">("");

  const loadSimulators = async () => {
    const res = await request("/api/v1/simulators");
    if (res.ok) setSimulators(await res.json());
  };

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [authModel, setAuthModel] = useState("OAUTH2_CLIENT_CREDENTIALS");
  const [tokenTtl, setTokenTtl] = useState(3600);
  const [autoApprove, setAutoApprove] = useState(true);
  const [scopes, setScopes] = useState("");
  const [strawbayClientId, setStrawbayClientId] = useState("");
  const [strawbayClientSecret, setStrawbayClientSecret] = useState("");
  const [showStrawbaySecret, setShowStrawbaySecret] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await request("/api/v1/config");
      if (res.ok) {
        const data = await res.json();
        setConfig(data);
        setName(data.name ?? "");
        setDescription(data.description ?? "");
        setAuthModel(data.auth_model ?? "OAUTH2_CLIENT_CREDENTIALS");
        setTokenTtl(data.token_ttl ?? 3600);
        setAutoApprove(data.auto_approve ?? true);
        setScopes((data.scopes ?? []).join(", "));
        setStrawbayClientId(data.strawbay_client_id ?? "");
        setStrawbayClientSecret(data.strawbay_client_secret ?? "");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    loadSimulators();
  }, []);

  const activeSimulator = simulators.find((s) => s.is_active) || null;

  const handleSelectSimulator = async (id: number) => {
    if (!id || (activeSimulator && id === activeSimulator.id)) return;
    setSwitching(true);
    setError(null);
    try {
      const res = await request(`/api/v1/simulators/${id}/load`, "POST");
      if (res.ok) {
        await loadSimulators();
        await load();
        setSuccess(true);
        window.dispatchEvent(new CustomEvent("simulator-config-saved"));
      } else {
        setError("Failed to switch simulator.");
      }
    } finally {
      setSwitching(false);
    }
  };

  const handleDecommission = async (s: SimEntry) => {
    if (
      !window.confirm(
        `Decommission "${s.name}"? This soft-deletes it. If it's the active one, the most recently updated other simulator becomes active.`
      )
    )
      return;
    setDecommissioning(s.id);
    setError(null);
    try {
      const res = await request(`/api/v1/simulators/${s.id}/decommission`, "POST");
      if (res.ok) {
        setAdminSelectedId("");
        await loadSimulators();
        if (s.is_active) {
          await load();
          window.dispatchEvent(new CustomEvent("simulator-config-saved"));
        }
      } else {
        const body = await res.json().catch(() => ({}));
        setError(body.error || "Failed to decommission simulator.");
      }
    } finally {
      setDecommissioning(null);
    }
  };

  const adminSelected = simulators.find((s) => s.id === adminSelectedId) || null;

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      const scopesArr = scopes.split(",").map((s) => s.trim()).filter(Boolean);
      const res = await request("/api/v1/config", "PATCH", {
        name,
        description,
        auth_model: authModel,
        token_ttl: tokenTtl,
        auto_approve: autoApprove,
        scopes: scopesArr,
        strawbay_client_id: strawbayClientId,
        strawbay_client_secret: strawbayClientSecret,
      });
      if (res.ok) {
        setConfig(await res.json());
        setSuccess(true);
        window.dispatchEvent(new CustomEvent("simulator-config-saved"));
      } else {
        setError("Failed to save settings.");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleRotate = async () => {
    if (!window.confirm("Rotate credentials? All existing tokens will be revoked.")) return;
    setRotating(true);
    try {
      const res = await request("/api/v1/config/rotate", "POST");
      if (res.ok) await load();
    } finally {
      setRotating(false);
    }
  };

  const handleRevokeTokens = async () => {
    if (!window.confirm("Revoke all active tokens?")) return;
    await request("/api/v1/config/revoke-tokens", "POST");
  };

  const handleExport = async () => {
    const res = await request("/api/v1/config/export");
    if (!res.ok) return;
    const blob = await res.blob();
    const disposition = res.headers.get("Content-Disposition") || "";
    const match = disposition.match(/filename="([^"]+)"/);
    const filename = match ? match[1] : "simulator_config.json";
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    setError(null);
    setSuccess(false);
    try {
      const text = await file.text();
      const body = JSON.parse(text);
      const res = await request("/api/v1/config/import", "POST", body);
      if (res.ok) {
        await load();
        setSuccess(true);
      } else {
        setError("Failed to import configuration.");
      }
    } catch {
      setError("Invalid configuration file.");
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleApplySuggestions = (fields: SuggestedInfo) => {
    if (fields.name != null) setName(fields.name);
    if (fields.description != null) setDescription(fields.description);
    if (fields.authorization_type != null) {
      const mapped = AUTH_TYPE_MAP[fields.authorization_type];
      if (mapped) setAuthModel(mapped);
    }
    setSuccess(false);
    setError(null);
  };

  const handleReset = async () => {
    setResetting(true);
    setError(null);
    try {
      const res = await request("/api/v1/config/reset", "POST");
      if (res.ok) {
        setResetOpen(false);
        await load();
        setSuccess(true);
      } else {
        setError("Failed to reset simulator.");
      }
    } finally {
      setResetting(false);
    }
  };

  const copy = (text: string) => navigator.clipboard?.writeText(text);

  if (loading) return <Box sx={{ p: 4 }}><CircularProgress size={24} /></Box>;

  return (
    <Box sx={{ p: 4, maxWidth: 640 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="h5" fontWeight={600}>
            Settings
          </Typography>
          <Chip
            label={SIMULATOR_TYPE.toUpperCase()}
            size="small"
            color={SIMULATOR_TYPE === "target" ? "primary" : "secondary"}
            sx={{ fontWeight: 600 }}
          />
        </Stack>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AutoFixHighIcon />}
          onClick={() => setSuggestOpen(true)}
          sx={{ textTransform: "none", borderColor: "#7166cf", color: "#7166cf", "&:hover": { borderColor: "#5a4dbd", color: "#5a4dbd" } }}
        >
          Suggest from docs
        </Button>
      </Stack>

      {/* Selected simulator — switching changes the active pointer */}
      <Box sx={{ mb: 3, p: 2, bgcolor: "#fff", border: "1px solid #e5e7eb", borderRadius: 1 }}>
        <Typography variant="caption" sx={{ color: "#6b7280", display: "block", mb: 1 }}>
          Selected Simulator
        </Typography>
        <FormControl size="small" fullWidth>
          <Select
            value={activeSimulator?.id ?? ""}
            onChange={(e) => handleSelectSimulator(Number(e.target.value))}
            disabled={switching || simulators.length === 0}
            displayEmpty
          >
            {simulators.length === 0 && (
              <MenuItem value="">
                <em>No saved simulators</em>
              </MenuItem>
            )}
            {simulators.map((s) => (
              <MenuItem key={s.id} value={s.id}>
                {s.name || `(unnamed #${s.id})`}
                {s.description ? ` — ${s.description}` : ""}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {switching && (
          <LinearProgress sx={{ mt: 1, height: 2, borderRadius: 1 }} />
        )}
      </Box>

      {success && <Alert severity="success" sx={{ mb: 2 }}>Saved.</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Simulator name */}
      <Box sx={{ mb: 3, p: 2, bgcolor: "#fff", border: "1px solid #e5e7eb", borderRadius: 1 }}>
        <Typography variant="subtitle2" mb={2}>Simulator name</Typography>
        <TextField
          size="small"
          fullWidth
          placeholder="e.g. Fortnox Production"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <TextField
          size="small"
          fullWidth
          multiline
          rows={2}
          placeholder="Optional description of what this simulator represents"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          sx={{ mt: 2 }}
        />
      </Box>

      {/* Auth settings */}
      <Box sx={{ mb: 3, p: 2, bgcolor: "#fff", border: "1px solid #e5e7eb", borderRadius: 1 }}>
        <Typography variant="subtitle2" mb={2}>
          {OAUTH_MODELS.includes(authModel) ? "OAuth settings" : "Auth model"}
        </Typography>

        <Stack spacing={2}>
          <FormControl size="small" fullWidth>
            <InputLabel>Auth model</InputLabel>
            <Select
              label="Auth model"
              value={authModel}
              onChange={(e) => setAuthModel(e.target.value)}
            >
              <MenuItem value="OAUTH2_CLIENT_CREDENTIALS">OAuth 2.0 — Client Credentials</MenuItem>
              <MenuItem value="OAUTH2_AUTHORIZATION_CODE">OAuth 2.0 — Authorization Code</MenuItem>
              <MenuItem value="BEARER_TOKEN">Bearer Token (static)</MenuItem>
              <MenuItem value="BASIC">Basic Auth</MenuItem>
              <MenuItem value="BASIC_WITH_SESSION_TOKEN">Basic Auth with Session Token</MenuItem>
              <MenuItem value="QUERY_STRING">Query String (API key)</MenuItem>
            </Select>
          </FormControl>

          {OAUTH_MODELS.includes(authModel) && (
            <>
              <TextField
                label="Token TTL (seconds)"
                type="number"
                size="small"
                value={tokenTtl}
                onChange={(e) => setTokenTtl(parseInt(e.target.value) || 3600)}
                sx={{ maxWidth: 220 }}
              />
              <TextField
                label="Allowed scopes (comma-separated)"
                size="small"
                fullWidth
                placeholder="read, write, admin"
                value={scopes}
                onChange={(e) => setScopes(e.target.value)}
                helperText="Leave empty to accept any scope"
              />
              {authModel === "OAUTH2_AUTHORIZATION_CODE" && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={autoApprove}
                      onChange={(e) => setAutoApprove(e.target.checked)}
                      sx={{ "& .MuiSwitch-thumb": { bgcolor: autoApprove ? "#7166cf" : undefined } }}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2">Auto-approve OAuth flow</Typography>
                      <Typography variant="caption" color="text.secondary">
                        When on, /oauth/authorize redirects immediately without showing a consent screen
                      </Typography>
                    </Box>
                  }
                />
              )}
            </>
          )}
        </Stack>
      </Box>

      {/* Strawbay Engine Authentication — the client_id / client_secret pair
          this simulator uses when calling *back* into the Strawbay engine
          (sourced from Company → Flow Access Settings in the Console). */}
      <Box sx={{ mb: 3, p: 2, bgcolor: "#fff", border: "1px solid #e5e7eb", borderRadius: 1 }}>
        <Typography variant="subtitle2" mb={0.5}>
          Strawbay Engine Authentication
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
          Credentials this simulator presents when calling the Strawbay engine.
          Copy them from Company → Flow Access Settings in the Console.
        </Typography>
        <Stack spacing={2}>
          <TextField
            label="client_id"
            size="small"
            fullWidth
            placeholder="paste from Console"
            value={strawbayClientId}
            onChange={(e) => setStrawbayClientId(e.target.value)}
            sx={{ "& .MuiOutlinedInput-input": { fontFamily: "monospace", fontSize: 13 } }}
          />
          <TextField
            label="client_secret"
            size="small"
            fullWidth
            placeholder="paste from Console"
            type={showStrawbaySecret ? "text" : "password"}
            value={strawbayClientSecret}
            onChange={(e) => setStrawbayClientSecret(e.target.value)}
            sx={{ "& .MuiOutlinedInput-input": { fontFamily: "monospace", fontSize: 13 } }}
            InputProps={{
              endAdornment: (
                <IconButton
                  size="small"
                  onClick={() => setShowStrawbaySecret((v) => !v)}
                  aria-label={showStrawbaySecret ? "Hide client_secret" : "Show client_secret"}
                  edge="end"
                >
                  {showStrawbaySecret ? (
                    <VisibilityOffIcon fontSize="small" />
                  ) : (
                    <VisibilityIcon fontSize="small" />
                  )}
                </IconButton>
              ),
            }}
          />
        </Stack>
      </Box>

      {/* Save buttons — Save updates the active simulator;
          Save as new creates a copy under a new name. */}
      <Stack direction="row" spacing={1.5} justifyContent="flex-end" mb={3}>
        <Button
          variant="outlined"
          startIcon={<SaveAltIcon />}
          onClick={() => setSaveAsOpen(true)}
          sx={{ textTransform: "none" }}
        >
          Save as new…
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          sx={{ bgcolor: "#7166cf", "&:hover": { bgcolor: "#5a4dbd" }, textTransform: "none" }}
        >
          {saving ? <CircularProgress size={18} color="inherit" /> : "Save"}
        </Button>
      </Stack>

      {/* Credentials */}
      <Box sx={{ mb: 3, p: 2, bgcolor: "#fff", border: "1px solid #e5e7eb", borderRadius: 1 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="subtitle2">
            {authModel === "BASIC" || authModel === "BASIC_WITH_SESSION_TOKEN"
              ? "Credentials"
              : authModel === "QUERY_STRING"
              ? "API key credentials"
              : "OAuth credentials"}
          </Typography>
          <Button
            size="small"
            startIcon={rotating ? <CircularProgress size={14} /> : <RefreshIcon />}
            onClick={handleRotate}
            disabled={rotating}
            sx={{ textTransform: "none", color: "#c62828" }}
          >
            Rotate
          </Button>
        </Stack>
        <CredentialRow
          label={authModel === "BASIC" || authModel === "BASIC_WITH_SESSION_TOKEN" ? "username" : "client_id"}
          value={config?.client_id}
          onCopy={copy}
        />
        <CredentialRow
          label={authModel === "BASIC" || authModel === "BASIC_WITH_SESSION_TOKEN" ? "password" : "client_secret"}
          value={config?.client_secret}
          onCopy={copy}
          masked
        />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
          {authModel === "BASIC" || authModel === "BASIC_WITH_SESSION_TOKEN"
            ? "Use these as username and password in your application configuration."
            : authModel === "QUERY_STRING"
            ? "Use client_id as your API key in the query string."
            : "Use these in your application configuration. Rotate to invalidate all existing tokens."}
        </Typography>
      </Box>

      {/* Simulator Admin — pick a saved simulator and act on it */}
      <Box sx={{ mb: 3, p: 2, bgcolor: "#fff", border: "1px solid #e5e7eb", borderRadius: 1 }}>
        <Typography variant="subtitle2" mb={1}>Simulator Admin</Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Pick a saved simulator to view its details. Decommissioning soft-deletes
          — if it&apos;s the active one, a fresh default is auto-created.
        </Typography>
        <FormControl size="small" fullWidth>
          <Select
            value={adminSelectedId}
            onChange={(e) =>
              setAdminSelectedId(e.target.value === "" ? "" : Number(e.target.value))
            }
            displayEmpty
          >
            <MenuItem value="">
              <em>Select a simulator to manage…</em>
            </MenuItem>
            {simulators.map((s) => (
              <MenuItem key={s.id} value={s.id}>
                {s.name || `(unnamed #${s.id})`}
                {s.is_active ? " — active" : ""}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {adminSelected && (
          <Box
            sx={{
              mt: 2,
              p: 1.75,
              bgcolor: "#f9fafb",
              border: "1px solid #e5e7eb",
              borderRadius: 1,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1} mb={1}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: "#374151" }}>
                {adminSelected.name || `(unnamed #${adminSelected.id})`}
              </Typography>
              {adminSelected.is_active && (
                <Chip
                  label="active"
                  size="small"
                  color="success"
                  icon={<CheckCircleOutlineIcon fontSize="small" />}
                  sx={{ height: 20 }}
                />
              )}
            </Stack>
            {adminSelected.description && (
              <Typography variant="caption" sx={{ color: "#6b7280", display: "block", mb: 0.5 }}>
                {adminSelected.description}
              </Typography>
            )}
            <Typography
              variant="caption"
              sx={{ color: "#9ca3af", fontFamily: "monospace", display: "block" }}
            >
              client_id: {adminSelected.client_id}
            </Typography>
            {adminSelected.updated_at && (
              <Typography
                variant="caption"
                sx={{ color: "#9ca3af", display: "block", mb: 1.5 }}
              >
                updated {dayjs(adminSelected.updated_at).format("YYYY-MM-DD HH:mm")}
              </Typography>
            )}
            <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={1}>
              {simulators.length <= 1 && (
                <Typography variant="caption" sx={{ color: "#9ca3af" }}>
                  At least one simulator must remain
                </Typography>
              )}
              <Tooltip
                title={
                  simulators.length <= 1
                    ? "Save another simulator before decommissioning this one"
                    : ""
                }
                arrow
              >
                <span>
                  <Button
                    size="small"
                    color="error"
                    variant="outlined"
                    disabled={decommissioning != null || simulators.length <= 1}
                    startIcon={
                      decommissioning === adminSelected.id ? (
                        <CircularProgress size={14} />
                      ) : (
                        <ErrorOutlineIcon fontSize="small" />
                      )
                    }
                    onClick={() => handleDecommission(adminSelected)}
                    sx={{ textTransform: "none" }}
                  >
                    Decommission
                  </Button>
                </span>
              </Tooltip>
            </Stack>
          </Box>
        )}
      </Box>

      {/* Export / Import */}
      <Box sx={{ mb: 3, p: 2, bgcolor: "#fff", border: "1px solid #e5e7eb", borderRadius: 1 }}>
        <Typography variant="subtitle2" mb={1}>Configuration file</Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Export a complete snapshot of this simulator (name, auth settings, credentials, all endpoints).
          Import a previously exported file to restore a configuration.
        </Typography>
        <Stack direction="row" spacing={1.5}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            sx={{ textTransform: "none" }}
          >
            Download config
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={importing ? <CircularProgress size={14} /> : <UploadIcon />}
            onClick={() => fileInputRef.current?.click()}
            disabled={importing}
            sx={{ textTransform: "none" }}
          >
            {importing ? "Importing…" : "Upload config"}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/json,.json"
            style={{ display: "none" }}
            onChange={handleImport}
          />
        </Stack>
      </Box>

      <Divider sx={{ my: 2 }} />
      <Box sx={{ p: 2, border: "1px solid #fecaca", borderRadius: 1, bgcolor: "#fff5f5" }}>
        <Typography variant="subtitle2" color="error" mb={2}>Danger zone</Typography>
        <Stack spacing={1.5}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
            <Box>
              <Typography variant="body2" fontWeight={500}>Revoke all active tokens</Typography>
              <Typography variant="caption" color="text.secondary">
                Invalidates all issued access tokens. Credentials are not changed.
              </Typography>
            </Box>
            <Button
              variant="outlined"
              color="error"
              size="small"
              onClick={handleRevokeTokens}
              sx={{ textTransform: "none", flexShrink: 0 }}
            >
              Revoke tokens
            </Button>
          </Box>
          <Divider />
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
            <Box>
              <Typography variant="body2" fontWeight={500}>Reset simulator</Typography>
              <Typography variant="caption" color="text.secondary">
                Deletes all endpoints and logs, revokes all tokens. Name, description and credentials are kept.
              </Typography>
            </Box>
            <Button
              variant="contained"
              color="error"
              size="small"
              onClick={() => setResetOpen(true)}
              sx={{ textTransform: "none", flexShrink: 0 }}
            >
              Reset
            </Button>
          </Box>
        </Stack>
      </Box>

      <Dialog open={resetOpen} onClose={() => !resetting && setResetOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Reset simulator?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This will permanently delete all configured endpoints and request logs, and revoke all active tokens.
            Your simulator name, description, auth settings and credentials will not be changed.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => setResetOpen(false)}
            disabled={resetting}
            sx={{ textTransform: "none", color: "#6b7280" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleReset}
            disabled={resetting}
            sx={{ textTransform: "none" }}
          >
            {resetting ? <CircularProgress size={18} color="inherit" /> : "Reset simulator"}
          </Button>
        </DialogActions>
      </Dialog>

      <SuggestDialog
        open={suggestOpen}
        onClose={() => setSuggestOpen(false)}
        onApply={handleApplySuggestions}
        request={request}
      />

      <SaveAsDialog
        open={saveAsOpen}
        defaultName={name}
        defaultDescription={description}
        onClose={() => setSaveAsOpen(false)}
        onSaved={async () => {
          setSuccess(true);
          await loadSimulators();
        }}
      />
    </Box>
  );
};

const CredentialRow: React.FC<{
  label: string;
  value?: string;
  onCopy: (v: string) => void;
  masked?: boolean;
}> = ({ label, value, onCopy, masked }) => (
  <Stack direction="row" alignItems="center" spacing={1} mb={1}>
    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 100 }}>
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{ fontFamily: "monospace", fontSize: 13, flex: 1, wordBreak: "break-all" }}
    >
      {masked ? "••••••••••••••••" : value}
    </Typography>
    <Tooltip title="Copy">
      <IconButton size="small" onClick={() => onCopy(value || "")}>
        <ContentCopyIcon sx={{ fontSize: 14 }} />
      </IconButton>
    </Tooltip>
  </Stack>
);

export default Settings;
